if (!customElements.get('brincatoys-product-experience')) {
  class BrincatoysProductExperience extends HTMLElement {
    constructor() {
      super();
      window.initScriptOnDemand(this, this.init.bind(this));
    }

    connectedCallback() {
      // Heading navigation must work before the description enters the viewport.
      // Leave the other enhancements on the existing lazy initialization path.
      this.normalizeDescriptionHeadings();
    }

    init() {
      this.normalizeDescriptionHeadings();

      if (window.matchMedia('(max-width: 749px)').matches) {
        this.querySelectorAll('[data-bt-mobile-collapsed]').forEach((details) => {
          details.removeAttribute('open');
        });
      }

      this.querySelectorAll('[data-bt-copy-sku]').forEach((button) => {
        button.addEventListener('click', () => this.copySku(button));
      });
    }

    normalizeDescriptionHeadings() {
      this.querySelectorAll('.bt-product-description .product__description').forEach((description) => {
        if (description.hasAttribute('data-bt-heading-levels-normalized')) return;

        // Keep the source outline as well as the effective one, so repeated H4s
        // below the description's H2 remain siblings when they become H3s.
        const outline = [];
        description.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
          const sourceLevel = Number(heading.tagName.slice(1));
          while (outline.length && outline[outline.length - 1].sourceLevel >= sourceLevel) outline.pop();
          const parentLevel = outline.length ? outline[outline.length - 1].effectiveLevel : 2;
          const effectiveLevel = sourceLevel <= 2 ? sourceLevel : Math.min(sourceLevel, parentLevel + 1);
          outline.push({ sourceLevel, effectiveLevel });
          if (sourceLevel === effectiveLevel) return;

          const appearance = window.getComputedStyle(heading);
          const fontSize = Number.parseFloat(appearance.fontSize);
          const relativeLength = (value) => {
            const length = Number.parseFloat(value);
            return value.endsWith('px') && Number.isFinite(length) && fontSize > 0
              ? `${length / fontSize}em`
              : value;
          };
          const replacement = document.createElement(`h${effectiveLevel}`);
          Array.from(heading.attributes).forEach(({ name, value }) => replacement.setAttribute(name, value));
          replacement.classList.add(`h${sourceLevel}`);
          replacement.setAttribute('data-bt-original-heading-level', sourceLevel);

          // Relative measures follow the original heading's responsive size.
          // The scoped CSS also avoids the H3 balance rule and the description's
          // H1-H4 styling changing an original H4, H5 or H6's appearance.
          replacement.style.setProperty('--bt-description-heading-margin-start', relativeLength(appearance.marginBlockStart || appearance.marginTop));
          replacement.style.setProperty('--bt-description-heading-margin-end', relativeLength(appearance.marginBlockEnd || appearance.marginBottom));
          replacement.style.setProperty('--bt-description-heading-line-height', relativeLength(appearance.lineHeight));
          replacement.style.setProperty('--bt-description-heading-letter-spacing', relativeLength(appearance.letterSpacing));
          replacement.style.setProperty('--bt-description-heading-weight', appearance.fontWeight);
          replacement.style.setProperty('--bt-description-heading-color', appearance.color);
          replacement.style.setProperty('--bt-description-heading-text-wrap', appearance.getPropertyValue('text-wrap') || 'wrap');
          while (heading.firstChild) replacement.appendChild(heading.firstChild);
          heading.replaceWith(replacement);
        });

        description.setAttribute('data-bt-heading-levels-normalized', '');
      });
    }

    async copySku(button) {
      const sku = button.closest('dd')?.querySelector('.product__sku')?.textContent?.trim();
      const status = button.parentElement?.querySelector('[data-bt-copy-status]');
      if (!sku || !navigator.clipboard) return;

      try {
        await navigator.clipboard.writeText(sku);
        button.classList.add('is-copied');
        if (status) status.textContent = button.dataset.copiedText || '';
        window.setTimeout(() => button.classList.remove('is-copied'), 1600);
      } catch (_error) {
        // The SKU remains selectable when clipboard permissions are unavailable.
      }
    }
  }

  customElements.define('brincatoys-product-experience', BrincatoysProductExperience);
}
